import { Link } from 'react-router-dom';
import {
  BadgeCheck,
  CalendarClock,
  FileSearch,
  MessageCircle,
  Tags,
  Webhook,
} from 'lucide-react';
import { usePageMeta } from '../usePageMeta';
import { AgentLedger } from '../components/AgentLedger';
import { SectionHeading } from '../components/SectionHeading';
import { CtaBanner } from '../components/CtaBanner';

const CAP_TONES = ['g-blue', 'g-violet', 'g-pink', 'g-sun'] as const;

const CAPABILITIES = [
  {
    icon: MessageCircle,
    title: 'Answers every query, 24/7',
    body: 'Incoming WhatsApp messages get a real answer at any hour — the agent never sleeps, queues, or forgets.',
  },
  {
    icon: FileSearch,
    title: 'Grounded in your documents',
    body: 'RAG over your PDFs — catalogs, price lists, policies — so replies cite your actual material instead of guessing.',
  },
  {
    icon: Webhook,
    title: 'Takes real actions',
    body: 'The agent calls your APIs: checks stock, drafts orders, updates records. Answering is the start, not the end.',
  },
  {
    icon: CalendarClock,
    title: 'Follows up with leads',
    body: 'Warm conversations get scheduled follow-ups automatically — no lead goes cold because someone was busy.',
  },
  {
    icon: Tags,
    title: 'Tags and organizes contacts',
    body: 'Every incoming contact is classified and tagged as it arrives, so your pipeline stays clean by itself.',
  },
  {
    icon: BadgeCheck,
    title: 'Official WhatsApp API',
    body: 'Built on the official WhatsApp Business API — no gray-market gateways, no risk to your number.',
  },
] as const;

export function Pulse(): React.JSX.Element {
  usePageMeta(
    'Pulse — WhatsApp automation with agentic AI by Inafana',
    'Pulse puts an agentic AI on your WhatsApp number, 24/7: it answers from your documents with RAG, calls your APIs, tags contacts, and follows up with leads — on the official WhatsApp API.',
  );

  return (
    <>
      <section className="product-hero">
        <div className="blob blob-violet" style={{ width: 560, height: 560, top: -160, left: '-6%' }} />
        <div className="blob blob-pink" style={{ width: 440, height: 440, bottom: -120, right: '-4%' }} />
        <div className="container">
          <div className="product-hero-grid">
            <div className="product-hero-copy">
              <img
                className="product-mark"
                src="/assets/products/pulse.svg"
                alt="Pulse"
              />
              <h1 className="h1 reveal" data-delay="1">
                Your WhatsApp,
                <br />
                <span className="grad">answered at 3 a.m.</span>
              </h1>
              <p className="lede reveal" data-delay="2">
                Pulse puts an agentic AI on your business WhatsApp number. It answers
                from your documents, takes actions through your systems, tags contacts,
                and follows up with leads — around the clock, on the official API.
              </p>
              <div className="hero-ctas reveal" data-delay="3">
                <Link to="/#contact" className="btn btn-primary btn-lg">
                  Get a Pulse demo <span className="arrow">→</span>
                </Link>
              </div>
            </div>
            <div className="reveal" data-delay="2">
              <AgentLedger variant="expanded" title="Pulse — one overnight shift" />
            </div>
          </div>
        </div>
      </section>

      <section className="section-surface">
        <div className="container">
          <SectionHeading
            anim="blur"
            title={
              <>
                An agent, <span className="si accent-text">not an autoresponder</span>.
              </>
            }
            lede="Canned replies answer questions. Pulse resolves them — and does the work that follows."
          />
          <div className="capability-grid">
            {CAPABILITIES.map((c, i) => (
              <article key={c.title} className="module-card reveal" data-delay={(i % 3) + 1}>
                <div className={`icon ${CAP_TONES[i % CAP_TONES.length]}`} aria-hidden="true">
                  <c.icon size={20} strokeWidth={1.8} />
                </div>
                <h3 className="h3">{c.title}</h3>
                <p>{c.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section>
        <div className="container">
          <div className="feature-split">
            <div className="reveal">
              <h2 className="h2">Three steps to an agent <span className="si accent-text">on your number</span>.</h2>
              <p className="lede">
                Pulse connects to your existing business number through the official
                WhatsApp Business API — your customers keep messaging the number they
                already know.
              </p>
            </div>
            <ul className="feature-list reveal" data-delay="1">
              <li>
                <BadgeCheck size={20} strokeWidth={1.8} />
                <div>
                  <b>1 · Connect your number</b>
                  <span>
                    We set up the official WhatsApp Business API on your number and
                    connect the systems the agent should act on.
                  </span>
                </div>
              </li>
              <li>
                <FileSearch size={20} strokeWidth={1.8} />
                <div>
                  <b>2 · Load your knowledge</b>
                  <span>
                    Catalogs, price lists, policies — the PDFs and data your team
                    answers from become the agent's knowledge base.
                  </span>
                </div>
              </li>
              <li>
                <MessageCircle size={20} strokeWidth={1.8} />
                <div>
                  <b>3 · The agent takes the shift</b>
                  <span>
                    Pulse answers, acts, tags, and follows up — 24/7. Your team steps
                    in only where a human matters.
                  </span>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <CtaBanner
        title={
          <>
            Put an agent <span className="si">on your number</span>.
          </>
        }
        lede="Tell us what your customers ask and what should happen next. We'll show you Pulse handling it."
        primaryLabel="Get a Pulse demo"
      />
    </>
  );
}
